import { Form } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FC, useMemo } from 'react';
import * as Yup from 'yup';
import { Box, Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { FormContainer } from '../../../common/form/FormContainer';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { SectionHeader } from '../../../common/SectionHeader';
import { invitationLock, useInvitation } from './invitationFunctions';
import { entryRequestService } from '@api/services/services';
import { InvitationPersonAutocomplete } from '../../../common/form/InvitationPersonAutocomplete';
import { DatePicker } from '../../../common/form/DatePicker';
import { QcsTimePicker } from '../../../common/form/QcsTimePicker';
import { EstablishmentAutocomplete } from '../../../common/form/EstablishmentAutocomplete';
import { InvitationWorkplaceAutocomplete } from '../../../common/form/InvitationWorkplaceAutocomplete';
import { validations } from '../../../../utils/validations';
import { CreateUpdateEntryRequest } from '@api/models/request/invitation/CreateUpdateEntryRequest';
import { ShowError } from '../../../ShowError';
import { resources } from '@api/resources';
import { useAppDispatch } from '../../../../store';
import { CancelToken } from 'axios';
import { entryRequestListActions } from '../../../../store/entities/entryRequestList';
import { entryRequestInvitationListActions } from '../../../../store/entities/entryRequestInvitationList';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { InvitationState } from '@api/models/enums/InvitationState';

interface Props {
  data?: CreateUpdateEntryModal;
  onClose: () => void;
}

export interface CreateUpdateEntryModal {
  id?: string; // UUID
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
  timeFrom?: string; // ISO time string
  establishment?: ReferenceDto; // UUID
  supplierEmployees?: InvitedPersonDto[]; // UUID[]
  workplaces: ReferenceDto[]; // UUID[]
}

export const RequestEntryModal: FC<Props> = ({ data, onClose }) => {
  const { t, invitation, callWithLock } = useInvitation();
  const dispatch = useAppDispatch();

  const establishments = useMemo(
    () =>
      invitation.workplaces
        ?.filter((it) => it.establishment)
        .map((it) => it.establishment!)
        .filter((e, i, arr) => arr.findIndex((x) => x.id === e.id) === i) ?? [],
    [invitation.workplaces]
  );

  const handleRefresh = (cancelToken?: CancelToken) => {
    dispatch(entryRequestListActions.reload(true));
    dispatch(entryRequestInvitationListActions.reload(true));
  };

  const handleSubmit = async (formData: CreateUpdateEntryModal) => {
    await callWithLock(async () => {
      const data: CreateUpdateEntryRequest = {
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        timeFrom: formData.timeFrom,
        establishmentId: formData.establishment?.id,
        supplierEmployeeIds: formData.supplierEmployees?.map((se) => se.employeeId!),
        workplaceIds: formData.workplaces?.map((wp) => wp.id),
      };

      if (formData.id) {
        await entryRequestService.updateEntryRequest(formData.id, data);
        handleRefresh();
        onClose();
        return;
      }

      await entryRequestService.createEntryRequest(invitation.id!, data);
      handleRefresh();
      onClose();
    });
  };

  return (
    <QcsModal open={true} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title={'invitation.entryRequest.' + (data?.id ? 'edit' : 'add')} />
        <FormikDebounce<CreateUpdateEntryModal>
          initialValues={{
            id: data?.id || undefined,
            dateFrom: data?.dateFrom || '',
            dateTo: data?.dateTo || '',
            timeFrom: data?.timeFrom || '',
            supplierEmployees: data?.supplierEmployees || [],
            workplaces: data?.workplaces || [],
            establishment: data?.establishment,
          }}
          validationSchema={Yup.object().shape({
            supplierEmployees: validations.arrayNotEmpty(t),
            workplaces: validations.arrayNotEmpty(t),
            establishment: validations.objectRequired(t),
            dateFrom: validations.dateRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <FormContainer>
                <InvitationPersonAutocomplete
                  name="supplierEmployees"
                  label={t('invitation.entryRequest.selectedWorkers')}
                  multiple
                  required
                  persons={invitation.persons || []}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <DatePicker
                    name="dateFrom"
                    label={t('invitation.entryRequest.from')}
                    disablePast
                    required
                    onChange={(data) => setFieldValue('dateTo', data)}
                  />
                  <DatePicker name="dateTo" label={t('invitation.entryRequest.to')} disablePast />
                  <QcsTimePicker label={t('invitation.entryRequest.entryTime')} name="timeFrom" />
                </Box>

                <EstablishmentAutocomplete
                  establishments={establishments}
                  name="establishment"
                  label={t('invitation.entryRequest.establishment')}
                  required
                  onChange={(value) => {
                    const establishment = value as ReferenceDto | null;
                    // Auto-select of a single establishment also calls onChange - keep workplaces when nothing changed
                    if (establishment?.id !== values.establishment?.id) {
                      setFieldValue(
                        'workplaces',
                        establishment
                          ? values.workplaces.filter((wp) =>
                              invitation.workplaces?.some(
                                (iw) => iw.workplace?.id === wp.id && iw.establishment?.id === establishment.id,
                              ),
                            )
                          : [],
                      );
                    }
                    setFieldValue('establishment', establishment);
                  }}
                />

                <InvitationWorkplaceAutocomplete
                  disabled={!values.establishment}
                  multiple
                  name="workplaces"
                  label={t('invitation.entryRequest.workplaces')}
                  required
                  establishmentIds={values.establishment ? [values.establishment.id] : []}
                  showEstablishmentName={true}
                />

                <ShowError resource={data?.id ? resources.updateEntryRequest : resources.createEntryRequest} />
                <SubmitButton disabled={invitation.state === InvitationState.Archived || invitationLock.locked} />
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
